import React, { useState, useEffect } from 'react';
import { staffService as authService } from '../services/dataService';
import { isOnline } from '../services/syncService';

const isDesktop = !!window.menuronDesktop?.isDesktop;

const WaiterManagement = ({ restaurant }) => {
  const [waiters, setWaiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddWaiter, setShowAddWaiter] = useState(false);
  const [waiterForm, setWaiterForm] = useState({
    name: '',
    phone: '',
    password: ''
  });
  const [waiterErrors, setWaiterErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showWaiterPassword, setShowWaiterPassword] = useState(false);

  useEffect(() => {
    
    if (restaurant && restaurant.id) {
      loadWaiters();
    } else {
      setLoading(false);
    }
  }, [restaurant]);

  const loadWaiters = async () => {
    console.log('=== LOAD WAITERS DEBUG ===');
    console.log('Restaurant ID:', restaurant?.id);
    
    if (!restaurant || !restaurant.id) {
      console.error('No restaurant ID available');
      setLoading(false);
      return;
    }
    
    try {
      const { data, error } = await authService.getRestaurantWaiters(restaurant.id);


      if (error) {
        throw error;
      }
      
      setWaiters(data || []);
    } catch (error) {
      
      setWaiters([]);
    } finally {
      setLoading(false);
    }
  };
  const validateWaiterForm = () => {
    const errors = {};
    
    if (!waiterForm.name.trim()) {
      errors.name = 'Waiter name is required';
    }
    
    if (!waiterForm.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(waiterForm.phone.replace(/\D/g, ''))) {
      errors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    if (!waiterForm.password.trim()) {
      errors.password = 'Password is required';
    } else if (waiterForm.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setWaiterErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddWaiter = async () => {
    if (!validateWaiterForm()) return;

    

   setSubmitting(true);
    try {
      const { data, error } = await authService.addWaiter(restaurant.id, {
        name: waiterForm.name.trim(),
        phone: waiterForm.phone.trim(),
        password: waiterForm.password
      });

      if (error) {
        setWaiterErrors({ general: error.message });
        return;
      }

      setWaiterForm({ name: '', phone: '', password: '' });
      setWaiterErrors({});
      setShowAddWaiter(false);
      setWaiters(prev => [data, ...prev]);
      window.menuronDesktop?.local?.upsertWaiter(data);
      
    } catch (error) {
      
      setWaiterErrors({ general: 'Failed to add waiter. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };
  const toggleWaiterStatus = async (waiterId, currentStatus) => {
    try {
      const { error } = await authService.updateWaiterStatus(waiterId, !currentStatus);
      if (error) throw error;
      setWaiters(prev => prev.map(w => {
        if (w.id !== waiterId) return w;
        const updated = { ...w, is_active: !currentStatus };
        window.menuronDesktop?.local?.upsertWaiter(updated);
        return updated;
      }));
    } catch (error) {
      alert('Failed to update waiter status');
    }
  };

  const deleteWaiter = async (waiterId, waiterName) => {
    if (!window.confirm(`Are you sure you want to delete ${waiterName}? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await authService.deleteWaiter(waiterId);
      if (error) throw error;
      setWaiters(prev => prev.filter(w => w.id !== waiterId));
      window.menuronDesktop?.local?.removeWaiter(waiterId);
    } catch (error) {
      alert('Failed to delete waiter');
    }
  };

  // Debug render

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #FF4444',
          borderTop: '4px solid transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <h3>No restaurant data available</h3>
        <p>Please make sure you're logged in properly.</p>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      padding: '30px 20px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '30px',
          boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
          border: '2px solid rgba(255, 68, 68, 0.1)'
        }}>
          {/* Offline banner */}
          {isDesktop && !isOnline() && (
            <div style={{
              background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '10px',
              padding: '12px 16px', marginBottom: '20px',
              display: 'flex', alignItems: 'center', gap: '10px',
              color: '#92400e', fontSize: '0.9rem',
            }}>
              <span style={{ fontSize: '1.1rem' }}>⚠️</span>
              <span><strong>You're offline.</strong> Showing cached staff list. Adding or modifying waiters requires an internet connection.</span>
            </div>
          )}

          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px',
            paddingBottom: '20px',
            borderBottom: '2px solid #f8f9fa'
          }}>
            <div>
              <h2 style={{
                margin: '0 0 10px 0',
                color: '#333',
                fontSize: '2rem',
                fontWeight: '700'
              }}>
                Waiter Management
              </h2>
              <p style={{ margin: '0', color: '#666', fontSize: '1rem' }}>
                Manage your restaurant staff and their access
              </p>
            </div>
            {isDesktop && !isOnline() ? (
              <div title="Internet connection required to add waiters" style={{
                background: '#e5e7eb', color: '#9ca3af',
                border: 'none', padding: '15px 25px', borderRadius: '12px',
                fontSize: '1rem', fontWeight: '600', cursor: 'not-allowed',
              }}>
                + Add New Waiter
              </div>
            ) : (
              <button
                onClick={() => setShowAddWaiter(true)}
                style={{
                  background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
                  color: 'white', border: 'none', padding: '15px 25px',
                  borderRadius: '12px', fontSize: '1rem', fontWeight: '600',
                  cursor: 'pointer', boxShadow: '0 8px 25px rgba(255, 68, 68, 0.3)',
                  transition: 'all 0.3s ease',
                }}
              >
                + Add New Waiter
              </button>
            )}
          </div>

          {/* Waiters List */}
          {waiters.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 40px',
              background: '#f8f9fa',
              borderRadius: '15px',
              border: '2px dashed #dee2e6'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '20px' }}>👥</div>
              <h3 style={{ color: '#666', margin: '0 0 15px 0', fontSize: '1.3rem' }}>
                No Waiters Added Yet
              </h3>
              <p style={{ color: '#888', margin: '0', fontSize: '1rem' }}>
                {isDesktop && !isOnline()
                  ? 'Connect to the internet to add waiters.'
                  : 'Click "Add New Waiter" to add your first staff member'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '20px' }}>
              {waiters.map(waiter => (
                <div key={waiter.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '20px',
                  backgroundColor: '#fefefe',
                  borderRadius: '12px',
                  border: '2px solid #f1f3f4',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = '#FF6B00'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = '#f1f3f4'}>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.5rem',
                      fontWeight: '700'
                    }}>
                      {waiter.name.charAt(0).toUpperCase()}
                    </div>
                    
                    <div>
                      <h3 style={{
                        margin: '0 0 5px 0',
                        fontSize: '1.2rem',
                        fontWeight: '700',
                        color: '#333'
                      }}>
                        {waiter.name}
                      </h3>
                      <p style={{
                        margin: '0 0 5px 0',
                        color: '#666',
                        fontSize: '0.9rem'
                      }}>
                        📞 {waiter.phone}
                      </p>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <span style={{
                          fontSize: '0.8rem',
                          color: waiter.is_active ? '#28a745' : '#dc3545',
                          fontWeight: '600'
                        }}>
                          {waiter.is_active ? '● Active' : '● Inactive'}
                        </span>
                        <span style={{
                          fontSize: '0.8rem',
                          color: '#888'
                        }}>
                          Added: {new Date(waiter.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => toggleWaiterStatus(waiter.id, waiter.is_active)}
                      style={{
                        backgroundColor: waiter.is_active ? '#ffc107' : '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}
                    >
                      {waiter.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => deleteWaiter(waiter.id, waiter.name)}
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Waiter Modal - keeping the same as before */}
      {showAddWaiter && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: '1000'
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '15px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '1.4rem' }}>
              Add New Waiter
            </h3>

            {waiterErrors.general && (
              <div style={{
                background: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                color: '#dc2626',
                fontSize: '0.9rem'
              }}>
                {waiterErrors.general}
              </div>
            )}

            <div style={{ marginBottom: '15px' }}>
              <input
                type="text"
                placeholder="Waiter Name *"
                value={waiterForm.name}
                onChange={(e) => {
                  setWaiterForm({ ...waiterForm, name: e.target.value });
                  if (waiterErrors.name) setWaiterErrors({ ...waiterErrors, name: '' });
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: waiterErrors.name ? '2px solid #dc2626' : '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
              {waiterErrors.name && (
                <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
                  {waiterErrors.name}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '15px' }}>
              <input
                type="tel"
                placeholder="Phone Number *"
                value={waiterForm.phone}
                onChange={(e) => {
                  setWaiterForm({ ...waiterForm, phone: e.target.value });
                  if (waiterErrors.phone) setWaiterErrors({ ...waiterErrors, phone: '' });
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: waiterErrors.phone ? '2px solid #dc2626' : '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
              {waiterErrors.phone && (
                <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
                  {waiterErrors.phone}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '25px' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type={showWaiterPassword ? 'text' : 'password'}
                  placeholder="Password (min 6 characters) *"
                  value={waiterForm.password}
                  onChange={(e) => {
                    setWaiterForm({ ...waiterForm, password: e.target.value });
                    if (waiterErrors.password) setWaiterErrors({ ...waiterErrors, password: '' });
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 45px 12px 12px',
                    border: waiterErrors.password ? '2px solid #dc2626' : '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
                <button type="button" onClick={() => setShowWaiterPassword(!showWaiterPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex', alignItems: 'center' }}>
                  {showWaiterPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
              {waiterErrors.password && (
                <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
                  {waiterErrors.password}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAddWaiter(false);
                  setWaiterForm({ name: '', phone: '', password: '' });
                  setWaiterErrors({});
                }}
                disabled={submitting}
                style={{
                  padding: '12px 24px',
                  border: '2px solid #6c757d',
                  backgroundColor: 'transparent',
                  color: '#6c757d',
                  borderRadius: '8px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '1rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddWaiter}
                disabled={submitting}
                style={{
                  padding: '12px 24px',
                  backgroundColor: submitting ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '1rem'
                }}
              >
                {submitting ? 'Adding...' : 'Add Waiter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WaiterManagement;